import type { QueryData } from '#types';
import * as Sentry from '@sentry/nextjs';
import { BuilderError } from '@/types/errors';
type RestError = typeof Error.prototype & {
	response: {
		errors: {
			message: string;
		}[];
		status: string;
	};
};

type GenericDataProps = {
	id?: string;
	url?: string;
};

type WithData = {
	results?: QueryData<'data' | 'page', GenericDataProps>[];
};

export const restRequest = async <T>(
	modelName: string,
	query?: string,
	userAttributes?: string,
	fields?: string,
): Promise<T> => {
	try {
		const queryString = query ? `&query.${query}` : '';
		const userAttributesString = userAttributes ? `&userAttributes.${userAttributes}` : '';
		const fieldsString = fields ? `&fields=${fields}` : '';

		// use preview env var to set if we get only published data or all data.
		const preview = process.env.NEXT_PUBLIC_PREVIEW === 'true';

		const url =
			'https://cdn.builder.io/api/v3/content/' +
			`${modelName}/` +
			`?apiKey=${process.env.NEXT_PUBLIC_BUILDER_API}` +
			`${queryString}` +
			`${userAttributesString}` +
			`${fieldsString}` +
			'&limit=100' +
			`&includeUnpublished=${preview}&includeRefs=true&cachebust=true`;

		const data: WithData = await fetch(url).then((res) => res.json());

		// when we are in preview mode, we need to fetch all child data, because
		// builder.io does not have a way to fetch all unpublished child data.
		if (preview && data.results?.length) {
			const results: QueryData<'data' | 'page', GenericDataProps>[] = [];

			for (const item of data.results) {
				const previewUrl =
					'https://cdn.builder.io/api/v3/content/' +
					`${modelName}/${item.id}` +
					`?apiKey=${process.env.NEXT_PUBLIC_BUILDER_API}` +
					`&includeUnpublished=${preview}&includeRefs=true` +
					'&preview=true&noCache=true&cachebust=true';

				const previewData = await fetch(previewUrl).then((res) => res.json());
				results.push(previewData);
			}

			return results as T;
		}

		return data.results as T;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('error', error);

		Sentry.withScope((scope) => {
			scope.setTag('api', 'builder');
			scope.setLevel('error');
			Sentry.captureException(new BuilderError(error as Error));
		});

		const { response } = error as RestError;

		// This error will bubble up to [[...page]]
		if (response) {
			const messages = response.errors?.map((e) => e.message).join(' ');
			throw new BuilderError(`GraphQLClient returned: ${response.status}: ${messages}`);
		}

		throw error;
	}
};
